import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { reduxForm } from 'redux-form'
import { Col, Row } from 'react-bootstrap'
import { get, isEmpty } from 'lodash'
import { Button, Modal } from 'baby-design'
import { useSelector } from 'react-redux'

import { DangerAlert } from 'components/alerts'
import { DefaultButton, InfoButton } from 'components/buttons'
import Link from 'components/link'
import {
  Checkbox,
  Form,
  FormButtonGroup,
  Radio,
  RadioGroup,
  Select,
  Input,
} from 'components/forms'

import {
  cancelInsertCardOrder as onCancelOrder,
  saveInsertCardOrder,
  saveRegistry,
} from 'registry/actions'
import { createValidator } from 'lib/form-validation'
import {
  getEligibleForHelloBabyBox,
  getRegistryId,
  getShippingAddress,
  getUserFullName,
  getWantsInsertCards,
} from 'registry/reducers'
import ShippingFields, {
  createValidationRules as createShippingAddressValidationRules,
} from 'registry/forms/shipping-address-form/shipping-fields'
import { CONFIG, STATES_AND_PROVINCES } from 'shared/constants'
import InsertCardOrderSummary from './insert-card-order-summary'

import css from './insert-card-order-form.scss'

const QUANTITIES = CONFIG.registry.insertCardOrderQuantityOptions

// We only ship insert cards to the US and Canada.
const countries = ['United States', 'Canada']

const shippingAddressValidator = createValidator(
  createShippingAddressValidationRules({
    context: 'insertCardOrder.shippingAddress',
  })
)

export const validate = (data) => {
  if (data.registry.wantsInsertCards) {
    return shippingAddressValidator(data)
  }
  return {}
}

const InsertCardOrderFormView = ({
  error,
  fields: {
    registry: { wantsInsertCards },
    insertCardOrder: { quantity, shippingAddress },
  },
  isEligibleForHelloBabyBox,
  handleSubmit,
  hasRegistryShippingAddress,
  insertCardOrder,
  onHide,
  onCancelOrder,
  showWantsInsertCards = true,
  submitting,
}) => {
  const [cancelError, setCancelError] = useState(null)
  const [cancelSubmitting, setCancelSubmitting] = useState(false)
  const [showForm, setShowForm] = useState(!insertCardOrder)
  const [shouldValidate, setShouldValidate] = useState(true)
  const [isOpen, setIsOpen] = useState(false)
  const [recommendedAddress, setRecommendedAddress] = useState({})
  const formRef = useRef(null)

  const handleCancelInsertCardOrder = () => {
    setCancelSubmitting(true)
    onCancelOrder(insertCardOrder).catch((resp) => {
      setCancelError(resp)
      setCancelSubmitting(false)
    })
  }

  const originalRegistry = useSelector((state) => state.registry)

  const handleFormSubmit = (values, dispatch) => {
    const { insertCardOrder, registry } = values

    if (shouldValidate && recommendedAddress?.line1) {
      insertCardOrder.shippingAddress = {
        ...insertCardOrder.shippingAddress,
        streetAddress1: recommendedAddress.line1,
        streetAddress2: recommendedAddress.line2,
        city: recommendedAddress.city,
        state: recommendedAddress.state,
        zip: [recommendedAddress.zip, recommendedAddress.zipPlus4Code]
          .filter(Boolean)
          .join('-'),
      }
    }

    const registryData = { ...registry }
    if (
      (registry.wantsInsertCards || originalRegistry.wantsInsertCards) &&
      insertCardOrder.shippingAddress.isDefault
    ) {
      registryData.shippingAddress = insertCardOrder.shippingAddress
    }

    if (registry.wantsInsertCards || originalRegistry.wantsInsertCards) {
      return dispatch(
        saveInsertCardOrder({
          ...insertCardOrder,
          shouldValidate,
          registryId: registry.id || originalRegistry.id,
        })
      )
        .then((resp) => {
          if (insertCardOrder.id) {
            setShowForm(false)
          }
          return dispatch(saveRegistry({ registry: registryData }))
        })
        .catch((resp) => {
          setIsOpen(true)
          setRecommendedAddress(resp.recommendedAddress)
        })
    }

    // User does not want insert cards
    if (registry.wantsInsertCards == false) {
      return dispatch(saveRegistry({ registry: registryData }))
    }
  }

  const handleClose = () => {
    setIsOpen(false)
  }

  const line1Changed = (shippingAddress, recommendedAddress) => {
    return (
      shippingAddress.streetAddress1?.value?.toUpperCase() !==
      recommendedAddress?.line1?.toUpperCase()
    )
  }
  const line2Changed = (shippingAddress, recommendedAddress) => {
    return (
      shippingAddress.streetAddress2?.value?.toUpperCase() !==
      recommendedAddress?.line2?.toUpperCase()
    )
  }
  const line3Changed = (shippingAddress, recommendedAddress) => {
    return (
      shippingAddress.city?.value?.toUpperCase() !==
        recommendedAddress?.city?.toUpperCase() ||
      shippingAddress.state?.value?.toUpperCase() !==
        recommendedAddress?.state?.toUpperCase() ||
      shippingAddress.zip?.value !== recommendedAddress?.zip ||
      shippingAddress.zipPlus4Code?.value !== recommendedAddress?.zipPlus4Code
    )
  }

  const handleUseSelectedAddress = () => {
    if (formRef.current && formRef.current.props.onSubmit) {
      formRef.current.props.onSubmit()
    }
    setIsOpen(false)
  }

  return showForm ? (
    <>
      <Modal
        isOpen={isOpen}
        handleClose={() => {}}
        className={css.RecommendedAddressModal}
        primaryButton={
          <Button
            variant="primary"
            size="lg"
            onClick={handleUseSelectedAddress}
            className={css.RecommendedAddressModal__button}
          >
            Use this address
          </Button>
        }
        secondaryButton={
          <Button
            variant="secondary"
            size="lg"
            onClick={() => {
              setIsOpen(false)
              setShouldValidate(true)
            }}
            className={css.RecommendedAddressModal__button}
          >
            Edit original address
          </Button>
        }
      >
        <h1>Select an address</h1>
        <p>
          This address doesn't match postal records. We've provided a suggestion
          below.
        </p>
        <RadioGroup>
          <div className={css.RecommendedAddressModal__container}>
            <div className={css.RecommendedAddressModal__address}>
              <Radio
                value={shouldValidate}
                checked={shouldValidate}
                label="Recommended address"
                onChange={(e) => setShouldValidate(e.target.checked)}
              />
              <ul>
                <li>{shippingAddress.name?.value?.toUpperCase()}</li>
                <li
                  className={
                    line1Changed(shippingAddress, recommendedAddress)
                      ? css.RecommendedAddressModal__changed
                      : ''
                  }
                >
                  {recommendedAddress?.line1?.toUpperCase()}
                </li>
                <li
                  className={
                    line2Changed(shippingAddress, recommendedAddress)
                      ? css.RecommendedAddressModal__changed
                      : ''
                  }
                >
                  {recommendedAddress?.line2?.toUpperCase()}
                </li>
                <li
                  className={
                    line3Changed(shippingAddress, recommendedAddress)
                      ? css.RecommendedAddressModal__changed
                      : ''
                  }
                >
                  {recommendedAddress?.city?.toUpperCase()},{' '}
                  {recommendedAddress?.state?.toUpperCase()}{' '}
                  {[recommendedAddress?.zip, recommendedAddress?.zipPlus4Code]
                    .filter(Boolean)
                    .join('-')}
                </li>
                <li>{shippingAddress.country?.value?.toUpperCase()}</li>
              </ul>
            </div>
            <div className={css.RecommendedAddressModal__address}>
              <Radio
                value={!shouldValidate}
                checked={!shouldValidate}
                label="Original address"
                onChange={(e) => setShouldValidate(!e.target.checked)}
              />
              <ul>
                <li>{shippingAddress.name.value?.toUpperCase()}</li>
                <li>{shippingAddress.streetAddress1.value?.toUpperCase()}</li>
                <li>{shippingAddress.streetAddress2.value?.toUpperCase()}</li>
                <li>
                  {shippingAddress.city.value?.toUpperCase()},{' '}
                  {shippingAddress.state.value?.toUpperCase()}{' '}
                  {shippingAddress.zip.value}
                </li>
                <li>{shippingAddress.country?.value?.toUpperCase()}</li>
              </ul>
            </div>
          </div>
        </RadioGroup>
      </Modal>
      <Form
        className="welcome--insert-card-order-form mbl"
        error={error}
        onSubmit={handleSubmit(handleFormSubmit)}
        ref={formRef}
      >
        {insertCardOrder ? (
          <p>
            <strong>Order #IC-{insertCardOrder.id}</strong>
          </p>
        ) : showWantsInsertCards ? (
          <>
            <div className={isEligibleForHelloBabyBox ? 'mbs' : 'mbl'}>
              Would you like us to send you <strong>free</strong> registry
              insert cards to include in your shower invitations?
            </div>
            {isEligibleForHelloBabyBox ? (
              <p className="h6 mts mbl">
                (Psst..we'll include 30 free cards in your Hello Baby Box, if
                you've qualified, but you can always order more!)
              </p>
            ) : null}
            <RadioGroup horizontal>
              <Radio
                {...wantsInsertCards}
                value
                checked={wantsInsertCards.value == true}
                label="Yes, please!"
                onChange={(value) => wantsInsertCards.onChange(true)}
              />
              <Radio
                {...wantsInsertCards}
                checked={wantsInsertCards.value == false}
                label="No thanks, I'm sharing mine digitally."
                value={false}
                onChange={(value) => wantsInsertCards.onChange(false)}
              />
            </RadioGroup>
          </>
        ) : null}
        {wantsInsertCards.value || insertCardOrder ? (
          <>
            <Row>
              <Col sm={4}>
                <Select
                  label="How many do you need?"
                  {...quantity}
                  value={quantity.value}
                >
                  {QUANTITIES.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </Select>
              </Col>
            </Row>
            <p>
              <strong>Where should we ship them?</strong>
              <br />
              U.S. orders take 5 business days to ship. Canadian friends, please
              allow 10-14 business days for shipment. Need your cards sooner?{' '}
              <Link
                newWindow
                url="https://images.babylist.com/image/upload/v1722443567/babylist-insert-cards-printable-O_hh97hd_x_gv8yk0.pdf"
              >
                Download our pdf
              </Link>{' '}
              and print at home.
            </p>
            <Input
              name="shouldValidate"
              id="shouldValidate"
              value={shouldValidate}
              type="hidden"
            />
            <ShippingFields
              {...shippingAddress}
              countries={countries}
              excludedFields={['privateAddress']}
              states={STATES_AND_PROVINCES}
            />
            {!hasRegistryShippingAddress ? (
              <Checkbox
                {...shippingAddress.isDefault}
                checked={shippingAddress.isDefault.value == true}
                label="Make this your default shipping address (if gift-givers want to send directly to you)"
              />
            ) : null}
          </>
        ) : null}
        {insertCardOrder ? (
          <FormButtonGroup
            submitting={submitting}
            textPrimary="Update Order"
            onClickSecondary={() => setShowForm(false)}
          />
        ) : wantsInsertCards.value != null ? (
          <FormButtonGroup
            submitting={submitting}
            textPrimary={wantsInsertCards.value ? 'Place Order' : 'Save'}
            onClickSecondary={onHide}
          />
        ) : null}
      </Form>
    </>
  ) : (
    <div className="mbl">
      {cancelError && <DangerAlert>{cancelError._error}</DangerAlert>}
      <InsertCardOrderSummary insertCardOrder={insertCardOrder} />
      {insertCardOrder.status == 'new' ? (
        <div>
          <DefaultButton
            submitting={cancelSubmitting}
            onClick={handleCancelInsertCardOrder}
          >
            Cancel Order
          </DefaultButton>
        </div>
      ) : (
        <em>Your order has shipped!</em>
      )}
    </div>
  )
}

InsertCardOrderFormView.propTypes = {
  error: PropTypes.string,
  fields: PropTypes.shape({
    insertCardOrder: PropTypes.object.isRequired,
    registry: PropTypes.object.isRequired,
  }),
  formKey: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  hasRegistryShippingAddress: PropTypes.bool.isRequired,
  insertCardOrder: PropTypes.object,
  onHide: PropTypes.func,
  onCancelOrder: PropTypes.func.isRequired,
  onSubmitSuccess: PropTypes.func,
  showWantsInsertCards: PropTypes.bool,
  submitting: PropTypes.bool.isRequired,
}

const mapStateToProps = (state, ownProps) => {
  const { insertCardOrder } = ownProps
  const shippingAddress = getShippingAddress(state) || {}

  return {
    hasRegistryShippingAddress: !isEmpty(shippingAddress),
    isEligibleForHelloBabyBox: getEligibleForHelloBabyBox(state),
    initialValues: {
      insertCardOrder: {
        quantity: get(insertCardOrder, 'quantity') || QUANTITIES[0],
        shippingAddress: {
          city: shippingAddress.city,
          country: shippingAddress.country || countries[0],
          name: shippingAddress.name || getUserFullName(state),
          phone: shippingAddress.phone,
          state: shippingAddress.state,
          streetAddress1: shippingAddress.streetAddress1,
          streetAddress2: shippingAddress.streetAddress2,
          zip: shippingAddress.zip,
        },
        ...insertCardOrder,
      },
      registry: {
        id: getRegistryId(state),
        wantsInsertCards: getWantsInsertCards(state),
      },
    },
  }
}

export default reduxForm(
  {
    form: 'insertCardOrder',
    enableReinitialize: true,
    fields: [
      'insertCardOrder.id',
      'insertCardOrder.shippingAddress.city',
      'insertCardOrder.shippingAddress.country',
      'insertCardOrder.shippingAddress.name',
      'insertCardOrder.shippingAddress.phone',
      'insertCardOrder.shippingAddress.state',
      'insertCardOrder.shippingAddress.streetAddress1',
      'insertCardOrder.shippingAddress.streetAddress2',
      'insertCardOrder.shippingAddress.zip',
      'insertCardOrder.shippingAddress.isDefault',
      'insertCardOrder.quantity',
      'shouldValidate',
      'registry.id',
      'registry.wantsInsertCards',
    ],
    validate,
  },
  mapStateToProps,
  {
    onCancelOrder,
  }
)(InsertCardOrderFormView)
